// iter_83 — shared client-side order identity validator. Mirrors the
// backend `services/order_validation.py` rule set so the UI can show
// a friendly toast BEFORE we hit the network. Backend remains
// authoritative — server enforces the same rules.

const _str = (v) => (v == null ? "" : String(v).trim());

/**
 * @param {Object} args
 * @param {"dine_in"|"takeaway"|"pickup"|"delivery"} args.orderType
 * @param {"asap"|"scheduled"} [args.fulfilmentType]
 * @param {string|null} [args.tableNumber]
 * @param {string|null} [args.customerName]
 * @param {string|null} [args.customerPhone]
 * @param {string|null} [args.deliveryAddress]
 * @param {"pos"|"public"|"qr"|"kiosk"|"online"} [args.source]
 * @returns {{ ok: true } | { ok: false, error: string, field: string }}
 */
export function validateOrderIdentity({
  orderType,
  fulfilmentType = "asap",
  tableNumber,
  customerName,
  customerPhone,
  deliveryAddress,
  source = "pos",
}) {
  const name = _str(customerName);
  const phone = _str(customerPhone);
  const table = _str(tableNumber);
  const addr = _str(deliveryAddress);
  const isPreorder = fulfilmentType === "scheduled";

  // QR + dine-in: table number is enough; without it, fall back to
  // takeaway-style rules (need name + phone).
  if (source === "qr" && orderType === "dine_in") {
    if (table) return { ok: true };
    if (!name || !phone) {
      return { ok: false,
        error: "Customer name and phone are required for this QR order.",
        field: !name ? "customer_name" : "customer_phone" };
    }
    return { ok: true };
  }

  if (orderType === "dine_in") {
    if (!table && !name) {
      return { ok: false,
        error: "Enter a table number or customer name before completing this dine-in order.",
        field: "customer_name" };
    }
    // iter_84 — phone optional for POS dine-in (incl. preorder). Online/
    // kiosk dine-in is handled by the QR branch above when applicable.
    return { ok: true };
  }

  if (orderType === "takeaway" || orderType === "pickup") {
    if (!name) {
      return { ok: false, error: "Customer name is required.",
        field: "customer_name" };
    }
    // iter_84 — POS counter: phone optional. Online/kiosk: phone required.
    if (source !== "pos" && !phone) {
      return { ok: false,
        error: "Customer name and phone number are required.",
        field: "customer_phone" };
    }
    return { ok: true };
  }

  if (orderType === "delivery") {
    if (!name || !phone || !addr) {
      return { ok: false,
        error: "Customer name, phone number and delivery address are required for delivery orders.",
        field: !name ? "customer_name" : !phone ? "customer_phone" : "delivery_address" };
    }
    if (isPreorder && !addr) {
      return { ok: false,
        error: "Delivery address is required for delivery preorders.",
        field: "delivery_address" };
    }
    return { ok: true };
  }

  return { ok: true };
}
